import React from 'react';
import { motion } from 'framer-motion';
import { PresentationIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const SNAK_SCREEN = 'https://cdn.magicpatterns.com/patterns/generated-images/f56b4488-53cb-4caa-ae80-a720cbf1c3eb.jpg';
interface Channel {
  label: string;
  logo?: string;
  icon?: typeof PresentationIcon;
}
const LEFT_CHANNELS: readonly Channel[] = [{
  label: 'Email / CRM',
  logo: 'https://cdn.magicpatterns.com/uploads/eaVbSy8gkaLMxVXZn426xD/image.png'
}, {
  label: 'WhatsApp',
  logo: 'https://cdn.magicpatterns.com/uploads/ff2xrTo8njegc9HQfBiHHs/image.jpg'
}, {
  label: 'Website',
  logo: 'https://cdn.magicpatterns.com/uploads/p8hxGk8PzPNEJJ1JuXxQRJ/image.png'
}, {
  label: 'Social',
  logo: 'https://cdn.magicpatterns.com/uploads/i3HXhvnPCcq7MnMVtgzoGz/image.png'
}];
const RIGHT_CHANNELS: readonly Channel[] = [{
  label: 'QR code',
  logo: 'https://cdn.magicpatterns.com/uploads/5EBPJkAW8wtsVjG6Pdmtzv/image.png'
}, {
  label: 'Congress',
  logo: 'https://cdn.magicpatterns.com/uploads/gFpiinUzyP3uSTeNYjp6JX/image.png'
}, {
  label: 'Rep iPad / IVA',
  icon: PresentationIcon
}, {
  label: 'Paid media',
  logo: 'https://cdn.magicpatterns.com/uploads/x8cuoAZY8NxEd27vHPMXd5/image.png'
}];
function ChannelTile({
  channel,
  align,
  delay
}: {
  channel: Channel;
  align: 'left' | 'right';
  delay: number;
}) {
  return <motion.li initial={{
    opacity: 0,
    x: align === 'left' ? -20 : 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay,
    duration: 0.45
  }} className={`flex min-h-0 flex-1 items-center gap-[clamp(0.8rem,1.5vw,1.4rem)] rounded-2xl border border-[var(--fg)]/10 bg-white px-[clamp(1rem,1.8vw,1.75rem)] shadow-sm ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-90" data-mp="h9adhbkeluh4">
      {channel.icon ? <span className="flex h-[clamp(2.8rem,4vw,4.25rem)] w-[clamp(2.8rem,4vw,4.25rem)] flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--brand)]/10" data-id="element-91" data-mp="2s2my8plnh0ej">
          <channel.icon className="h-[clamp(1.35rem,2vw,2.1rem)] w-[clamp(1.35rem,2vw,2.1rem)] text-[var(--brand)]" aria-hidden="true" data-id="element-92" data-mp="3qf90gg8zsvt4" />
        </span> : <span className="flex h-[clamp(2.8rem,4vw,4.25rem)] w-[clamp(2.8rem,4vw,4.25rem)] flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--surface)] p-2" data-id="element-93" data-mp="38gev0d1bdx8">
          <img src={channel.logo} alt="" className="h-full w-full object-contain" data-id="element-94" data-mp="d7xi7b4tdfq1" />
        </span>}
      <span className={`min-w-0 flex-1 text-[clamp(1.15rem,1.9vw,1.8rem)] font-extrabold leading-tight tracking-[-0.03em] text-[var(--fg)] ${align === 'right' ? 'text-right' : ''}`} data-id="element-95" data-mp="qykl7fhlxrrx">
        {channel.label}
      </span>
    </motion.li>;
}
export function SlideChannelEntryPoints({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-97" data-mp="1fki5pkoqy98j">Meet HCPs wherever attention already exists.</SlideBanner>} data-id="element-96" data-mp="1fk8fpx3pc22u">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-98" data-mp="1khqtq1wkt7hp">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-99" data-mp="1ajuht7vte8ij">
          Distribution
        </p>
        <h2 className="max-w-4xl text-[clamp(1.8rem,3.4vw,3.1rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-100" data-mp="wrmm7jg9onlf">
          One experience.{' '}
          <span className="text-[var(--brand)]" data-id="element-101" data-mp="mwupsiyf86s8">Many entry points.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(0.9rem,1.8vw,1.6rem)] flex min-h-0 flex-1 items-stretch gap-[clamp(0.75rem,1.6vw,1.75rem)]" data-id="element-102" data-mp="runv79rwgwrg">
        <ul className="flex min-h-0 flex-1 flex-col gap-[clamp(0.45rem,0.9vw,0.85rem)]" data-id="element-103" data-mp="dwzbw6dhdxoi">
          {LEFT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="left" delay={0.25 + index * 0.08} data-id="element-104" data-mp="aryu07wkh0sm" data-mpi={index} />)}
        </ul>

        <motion.div initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15,
        duration: 0.55
      }} className="relative flex min-h-0 w-[20%] flex-shrink-0 flex-col items-center justify-center" data-id="element-105" data-mp="125it80c6now">
          <img src={SNAK_SCREEN} alt="A single Snak experience shared across every channel" className="min-h-0 max-h-full w-auto rounded-[1.25rem] object-contain drop-shadow-[0_22px_50px_rgba(23,32,51,0.2)]" data-id="element-106" data-mp="3j8aey6nprwso" />
          <span className="mt-[clamp(0.4rem,0.8vw,0.7rem)] flex-shrink-0 rounded-full bg-[var(--brand)] px-[clamp(0.6rem,1.1vw,1rem)] py-[clamp(0.2rem,0.4vw,0.35rem)] text-[clamp(0.55rem,0.8vw,0.74rem)] font-bold uppercase tracking-[0.18em] text-white" data-id="element-107" data-mp="39cqmxvc9ti97">
            One Snak
          </span>
        </motion.div>

        <ul className="flex min-h-0 flex-1 flex-col gap-[clamp(0.45rem,0.9vw,0.85rem)]" data-id="element-108" data-mp="1fygucbnuf0r2">
          {RIGHT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="right" delay={0.25 + index * 0.08} data-id="element-109" data-mp="1otmtirslykkm" data-mpi={index} />)}
        </ul>
      </div>
    </InteriorSlide>;
}